import React from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useCouncilAuth } from '../context/CouncilAuthContext';
import toast from 'react-hot-toast';

const NAV = [
  {
    to: '/council/dashboard', label: 'Dashboard', icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    ),
  },
  {
    to: '/council/meetings', label: 'Meetings & Minutes', icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    ),
  },
  {
    to: '/council/workorders', label: 'Work Orders', icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
    ),
  },
  {
    to: '/council/financials', label: 'Financials', icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
    ),
  },
];

export default function CouncilLayout() {
  const { council, logout } = useCouncilAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/council/login');
    toast.success('Signed out');
  };

  return (
    <div className="flex min-h-screen">
      <aside className="flex flex-col w-64 bg-emerald-900 min-h-screen">
        <div className="flex items-center gap-3 px-6 py-5 border-b border-emerald-700">
          <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center shrink-0">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <div>
            <span className="text-white font-bold text-base">Council Portal</span>
            <p className="text-emerald-300 text-xs">Read-only access</p>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.map(({ to, label, icon }) => (
            <NavLink key={to} to={to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-emerald-600 text-white'
                    : 'text-emerald-200 hover:bg-emerald-800 hover:text-white'
                }`
              }>
              <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {icon}
              </svg>
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="px-4 py-4 border-t border-emerald-700">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-white text-sm font-bold shrink-0">
              {council?.first_name?.[0]}{council?.last_name?.[0]}
            </div>
            <div className="overflow-hidden">
              <p className="text-sm text-white font-medium truncate">
                {council?.first_name} {council?.last_name}
              </p>
              <p className="text-xs text-emerald-300 truncate">Council Member</p>
            </div>
          </div>
          <button onClick={handleLogout}
            className="w-full text-left px-3 py-2.5 text-sm text-emerald-300 hover:text-white hover:bg-emerald-800 rounded-lg transition-colors">
            Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto bg-gray-50 p-4 md:p-8">
        <Outlet />
      </main>
    </div>
  );
}
